import { z } from "zod";

/** Otomasyon kuralları (Ayarlar → Otomasyon). Varsayılan hepsi kapalı. */
export const AUTOMATION_RULE_KEYS = [
  "price_alarm",
  "suspicious_order",
  "return_reminder",
  "low_stock_bestseller",
  "trendyol_auto_picking",
  "ideasoft_auto_approve",
  "ideasoft_ship_reminder",
  "channel_price",
] as const;
export type AutomationRuleKey = (typeof AUTOMATION_RULE_KEYS)[number];

export type AutomationField = "hours" | "percent" | "crossPercent" | "amount" | "minSold" | "mode" | "window" | "excludeBrands" | "brandPercents";
export type AutomationGroup = "guard" | "orders" | "price";
export const AUTOMATION_GROUPS: Record<AutomationGroup, { title: string; description: string }> = {
  guard: { title: "Koruma kuralları", description: "Yalnız bildirim gönderir, mağazaya hiçbir şey yazmaz." },
  orders: { title: "Sipariş kuralları", description: "Tekrarlayan sipariş adımlarını panel yapar." },
  price: { title: "Fiyat kuralları", description: "Kanallar arası fiyatı tutarlı tutar." },
};

export interface AutomationRuleDef {
  key: AutomationRuleKey;
  group: AutomationGroup;
  title: string;
  description: string;
  kind: "action" | "notify";
  channel: "ideasoft" | "trendyol" | null;
  fields: AutomationField[];
  /** "Şu an kurala uyan: N {unit}" */
  unit: string;
}

export const AUTOMATION_RULES: readonly AutomationRuleDef[] = [
  {
    key: "price_alarm",
    group: "guard",
    title: "Yanlış fiyat alarmı",
    description: "Satış fiyatı liste fiyatının belirlenen oranından fazla düşükse veya Trendyol fiyatı IdeaSoft fiyatının belirgin altına inerse önemli bildirim gider.",
    kind: "notify",
    channel: null,
    fields: ["percent", "crossPercent"],
    unit: "ürün",
  },
  {
    key: "suspicious_order",
    group: "guard",
    title: "Yüksek tutarlı ve şüpheli sipariş uyarısı",
    description: "Belirlenen tutarın üstündeki siparişte bildirim gider. Ayrıca aynı müşteriden 24 saatte toplamı tutarın yarısını geçen birden çok sipariş ve tutarın yarısını geçen siparişte teslimat ilinin fatura ilinden farklı olması da uyarılır.",
    kind: "notify",
    channel: null,
    fields: ["amount"],
    unit: "sipariş (son 2 gün)",
  },
  {
    key: "return_reminder",
    group: "guard",
    title: "Bekleyen iade hatırlatması",
    description: "Belirlenen süreyi geçen ve hâlâ karar verilmemiş iade talepleri için hatırlatma gider.",
    kind: "notify",
    channel: null,
    fields: ["hours"],
    unit: "iade talebi",
  },
  {
    key: "low_stock_bestseller",
    group: "guard",
    title: "Az kalan çok satan ürün uyarısı",
    description: "Son 30 günde belirlenen adetten fazla satan ürünün stoğu 1'e düşünce uyarı gider (haftada en fazla bir kez).",
    kind: "notify",
    channel: null,
    fields: ["minSold"],
    unit: "ürün",
  },
  {
    key: "trendyol_auto_picking",
    group: "orders",
    title: "Yeni Trendyol siparişlerinde Toplanmaya başla",
    description: "Yeni gelen Trendyol paketi otomatik “Toplanıyor” yapılır; anlaşmalı kargoda kargo kodu hemen oluşur ve etiket yazdırılabilir.",
    kind: "action",
    channel: "trendyol",
    fields: ["window", "excludeBrands"],
    unit: "sipariş",
  },
  {
    key: "ideasoft_auto_approve",
    group: "orders",
    title: "Ödemesi alınan IdeaSoft siparişini onayla",
    description: "“Onay bekliyor” durumundaki ve ödemesi başarılı IdeaSoft siparişleri otomatik “Onaylandı” yapılır.",
    kind: "action",
    channel: "ideasoft",
    fields: ["window", "excludeBrands"],
    unit: "sipariş",
  },
  {
    key: "ideasoft_ship_reminder",
    group: "orders",
    title: "Kargolanmayan IdeaSoft siparişi hatırlatması",
    description: "Onaylanıp belirlenen süre içinde kargoya verilmeyen IdeaSoft siparişleri için bildirim gönderilir.",
    kind: "notify",
    channel: "ideasoft",
    fields: ["hours"],
    unit: "sipariş",
  },
  {
    key: "channel_price",
    group: "price",
    title: "Kanal fiyat kuralı",
    description: "Trendyol fiyatı, IdeaSoft fiyatı + belirlenen yüzde olarak tutulur (tam TL'ye yukarı yuvarlanır). “Yalnız öner” modunda farklar listelenir, siz onaylarsınız; “Otomatik” modda panel Trendyol'u kendisi günceller.",
    kind: "action",
    channel: "trendyol",
    fields: ["percent", "brandPercents", "mode", "window", "excludeBrands"],
    unit: "fiyat farkı",
  },
];

export const ruleDef = (key: AutomationRuleKey) => AUTOMATION_RULES.find((r) => r.key === key)!;

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Saat SS:DD olmalı");
export const automationWindowSchema = z.object({ from: hhmm, to: hhmm }).refine((w) => w.from !== w.to, "Başlangıç ve bitiş aynı olamaz");
export const automationConfigSchema = z.object({
  enabled: z.boolean(),
  hours: z.coerce.number().int().min(2).max(240).optional(),
  percent: z.coerce.number().min(0).max(90).optional(),
  crossPercent: z.coerce.number().min(1).max(90).optional(),
  amount: z.coerce.number().int().min(1000).max(10_000_000).optional(),
  minSold: z.coerce.number().int().min(1).max(100).optional(),
  mode: z.enum(["suggest", "auto"]).optional(),
  window: automationWindowSchema.nullable().optional(),
  excludeBrands: z.array(z.string().trim().min(1).max(120)).max(100).optional(),
  brandPercents: z.record(z.string().trim().min(1).max(120), z.coerce.number().min(0).max(90)).optional(),
});
export type AutomationConfig = Omit<z.infer<typeof automationConfigSchema>, "enabled">;
export const automationSettingsSchema = z.object({ rules: z.partialRecord(z.enum(AUTOMATION_RULE_KEYS), automationConfigSchema.partial()) });

/** Kural başına varsayılan ayarlar. */
export const AUTOMATION_DEFAULTS: Record<AutomationRuleKey, AutomationConfig> = {
  price_alarm: { percent: 40, crossPercent: 10 },
  suspicious_order: { amount: 50_000 },
  return_reminder: { hours: 48 },
  low_stock_bestseller: { minSold: 2 },
  trendyol_auto_picking: { window: null, excludeBrands: [] },
  ideasoft_auto_approve: { window: null, excludeBrands: [] },
  ideasoft_ship_reminder: { hours: 24 },
  channel_price: { percent: 10, brandPercents: {}, mode: "suggest", window: null, excludeBrands: [] },
};

export interface AutomationRuleDto extends AutomationRuleDef {
  enabled: boolean;
  config: AutomationConfig;
  /** Şu an kurala uyan kayıt sayısı (önizleme). */
  matching: number;
  lastRun: { at: string; ok: number; failed: number; message: string | null } | null;
}
export interface AutomationResponse {
  rules: AutomationRuleDto[];
  canManage: boolean;
}

/** Deneme modu: "son 7 günde açık olsaydı" / "şu an uyanlar". */
export interface AutomationPreviewItem {
  label: string;
  detail: string;
  link: string | null;
  tone: "danger" | "warning" | null;
}
export interface AutomationPreviewDto {
  key: AutomationRuleKey;
  headline: string;
  note: string | null;
  total: number;
  items: AutomationPreviewItem[];
}

/** Çalışma geçmişi. */
export interface AutomationLogEntry {
  at: string;
  rule: AutomationRuleKey | "campaign" | "matching";
  ref: string;
  text: string;
  ok: boolean;
  link: string | null;
}
export const automationLogQuerySchema = z.object({ rule: z.enum([...AUTOMATION_RULE_KEYS, "campaign", "matching"]).optional() });
export interface AutomationLogResponse {
  items: AutomationLogEntry[];
}

/** Kanal fiyat kuralı: Trendyol'un hedef fiyattan farklı olduğu ürünler. */
export interface ChannelPriceDiffDto {
  listingId: string;
  productId: string;
  name: string;
  brand: string | null;
  sku: string;
  percent: number;
  ideasoft: { list: string; sale: string };
  trendyol: { list: string; sale: string };
  target: { list: string; sale: string };
}
export interface ChannelPriceResponse {
  connected: boolean;
  total: number;
  items: ChannelPriceDiffDto[];
}
export const channelPriceApplySchema = z.object({ listingIds: z.array(z.string().max(40)).min(1).max(200) });

/** Zamanlanmış kampanya (IdeaSoft indirimi; bitişte eski fiyata döner). */
export const campaignPreviewSchema = z.object({
  brands: z.array(z.string().trim().min(1).max(120)).min(1, "En az bir marka seçin").max(50),
  percent: z.coerce.number().int().min(1, "İndirim en az %1").max(70, "İndirim en fazla %70"),
  roundWhole: z.boolean().default(true),
});
export const campaignInputSchema = campaignPreviewSchema
  .extend({ name: z.string().trim().min(2, "Kampanya adı girin").max(80), startAt: z.iso.datetime({ offset: true }), endAt: z.iso.datetime({ offset: true }) })
  .refine((c) => Date.parse(c.endAt) > Date.parse(c.startAt), { message: "Bitiş başlangıçtan sonra olmalı", path: ["endAt"] })
  .refine((c) => Date.parse(c.endAt) - Date.parse(c.startAt) <= 62 * 24 * 3600_000, { message: "Kampanya en fazla 2 ay sürebilir", path: ["endAt"] });
export type CampaignInput = z.infer<typeof campaignInputSchema>;
export type CampaignStatus = "scheduled" | "running" | "done" | "cancelled" | "missed" | "failed";
export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  scheduled: "Planlandı",
  running: "Yayında",
  done: "Bitti",
  cancelled: "İptal edildi",
  missed: "Başlatılamadı",
  failed: "Hatalı",
};
export interface CampaignJobDto {
  status: string;
  total: number;
  done: number;
  ok: number;
  failed: number;
}
export interface CampaignDto {
  id: string;
  name: string;
  brands: string[];
  percent: number;
  roundWhole: boolean;
  startAt: string;
  endAt: string;
  status: CampaignStatus;
  /** İndirim uygulanan ürün sayısı (başladıysa). */
  items: number;
  /** Bitişte eski fiyatı geri yüklenmeyen ürün (o sırada elle değiştirilmiş). */
  skipped: number;
  message: string | null;
  createdBy: string | null;
  createdAt: string;
  startJob: CampaignJobDto | null;
  endJob: CampaignJobDto | null;
}
export interface CampaignsResponse {
  connected: boolean;
  canManage: boolean;
  items: CampaignDto[];
}
export interface CampaignPreviewDto {
  total: number;
  changed: number;
  sample: { name: string; brand: string | null; saleBefore: string; saleAfter: string }[];
}

// ───────────── Saf yardımcılar (sunucu ve arayüz) ─────────────

/** İstanbul saatiyle (UTC+3, yaz saati yok) gün içi dakika. */
export function istanbulMinutes(now: number): number {
  const d = new Date(now + 3 * 3600_000);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}
const toMin = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
/** Kural saat aralığında mı. Aralık yoksa her zaman; gece yarısını aşan aralık (22:00–06:00) desteklenir. */
export function inWindow(window: { from: string; to: string } | null | undefined, now: number): boolean {
  if (!window) return true;
  const m = istanbulMinutes(now);
  const from = toMin(window.from);
  const to = toMin(window.to);
  return from < to ? m >= from && m < to : m >= from || m < to;
}

/** Türkçe karakterleri sadeleştirip küçük harfe çevirir. */
export function foldTr(s: string): string {
  return s
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/â/g, "a")
    .replace(/î/g, "i")
    .replace(/û/g, "u");
}

/** Varsayılan hazır cevapların anahtar kelimeleri (şablon kimliğine göre). */
const TEMPLATE_KEYWORDS: Record<string, string[]> = {
  orijinal: ["orijinal", "original", "orjinal", "sahte", "replika", "cakma", "gercek mi", "kutu", "kutusu", "kutulu"],
  garanti: ["garanti", "garantili", "garantisi", "belge", "belgesi", "servis"],
  kargo: ["kargo", "kargoya", "kargolanir", "ne zaman", "teslim", "teslimat", "kac gun", "gonderim", "gonderi", "hizli"],
  su: ["su", "suya", "suda", "geçirmez", "gecirmez", "yuzme", "havuz", "dus", "deniz", "atm", "bar"],
};

/**
 * Soruya en uygun hazır cevap (yoksa null). Eşleşme: varsayılan anahtar kelimeler, şablonun
 * kendi anahtar kelimeleri (virgülle) ve başlığındaki kelimeler. Kısa kelimeler (≤3 harf) tam
 * kelime, uzunlar kelime başı olarak aranır.
 */
export function suggestTemplate<T extends { id: string; title: string; keywords?: string | null }>(question: string, templates: T[]): T | null {
  const text = ` ${foldTr(question).replace(/[^a-z0-9]+/g, " ")} `;
  const words = text.trim().split(" ");
  const hit = (kw: string) => {
    const k = foldTr(kw).replace(/[^a-z0-9]+/g, " ").trim();
    if (!k) return false;
    if (k.includes(" ")) return text.includes(` ${k} `);
    return k.length <= 3 ? words.includes(k) : words.some((w) => w.startsWith(k));
  };
  let best: T | null = null;
  let bestScore = 0;
  for (const t of templates) {
    const kws = new Set<string>([
      ...(TEMPLATE_KEYWORDS[t.id] ?? []),
      ...(t.keywords ?? "").split(",").map((s) => s.trim()).filter(Boolean),
      ...foldTr(t.title).split(/[^a-z0-9]+/).filter((w) => w.length >= 4),
    ]);
    let score = 0;
    for (const k of kws) if (hit(k)) score++;
    if (score > bestScore) {
      best = t;
      bestScore = score;
    }
  }
  return best;
}
